import { useTranslation } from "react-i18next";

import { TextButton } from "@industriousoffice/react-components";

import { Invoice, PaymentStatus } from "@/api/opb/opbTypes";
import PaymentStatusTag from "@/components/PaymentStatusTag";
import { useDateFormatter } from "@/hooks/useDateFormatter";

import { formatCurrency } from "../../../utils/string";

interface InvoiceTableRowProps {
  invoice: Invoice;
  currencyCode: string;
  description: string;
  withAction?: boolean;
  onAction?: (invoiceId: string) => void;
  actionText?: string;
  timezone?: string;
}

function InvoiceTableRow({
  invoice,
  currencyCode,
  description,
  withAction,
  onAction,
  actionText,
  timezone,
}: InvoiceTableRowProps) {
  const { format, formatDateFromYYYYMMDD } = useDateFormatter();
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <tr data-testid="invoice_table_tr">
      <td
        data-testid="invoice_table-issued_date-td"
        className="w-56 py-2 pl-4 text-sm text-left whitespace-normal border-b text-grey-30 font-hanken"
      >
        {format(invoice.createdAt, "PP", timezone)}
      </td>
      <td
        data-testid="invoice_table-number-td"
        className="w-56 py-2 pl-4 text-sm text-left whitespace-normal border-b text-grey-30 font-hanken"
      >
        {invoice.invoiceNumber}
      </td>
      <td
        data-testid="invoice_table-description-td"
        className="py-2 pl-4 text-sm text-left text-black whitespace-normal border-b w-xl font-hanken"
      >
        {description}
      </td>
      <td
        data-testid="invoice_table-amount-td"
        className="w-56 py-2 pl-4 text-sm font-bold text-left text-black whitespace-normal border-b font-hanken"
      >
        {formatCurrency({
          value:
            invoice.paymentStatus !== PaymentStatus.Paid
              ? invoice.balance
              : invoice.amount,
          currency: currencyCode,
          lang: language,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
      <td
        data-testid="invoice_table-due_date-td"
        className="w-56 py-2 pl-4 text-sm text-left whitespace-normal border-b text-grey-30 font-hanken"
      >
        {formatDateFromYYYYMMDD(invoice.dueDate, "PP")}
      </td>
      <td
        data-testid="invoice_table-status-td"
        className="w-56 py-2 pl-4 text-sm text-left whitespace-normal border-b font-hanken"
      >
        <PaymentStatusTag
          invoice={invoice}
          className="inline-block text-xs text-center font-hanken text-ellipsis"
        />
      </td>
      {withAction ? (
        <td
          data-testid="invoice_table-action-td"
          className="w-56 py-2 pl-4 text-sm text-left whitespace-normal border-b text-grey-30 font-hanken"
        >
          <TextButton type="button" onClick={() => onAction?.(invoice.id)}>
            {actionText}
          </TextButton>
        </td>
      ) : null}
    </tr>
  );
}

export default InvoiceTableRow;
